import { type ColumnVO, type IServerSideGetRowsRequest } from 'ag-grid-community';
import differenceWith from 'lodash/differenceWith';
import each from 'lodash/each';
import find from 'lodash/find';
import findKey from 'lodash/findKey';
import isEmpty from 'lodash/isEmpty';
import isUndefined from 'lodash/isUndefined';
import replace from 'lodash/replace';

import { composeQuery, select, f, and, orderBy, prerenderedSelect } from 'common/soql_builder';
import { type NamedSelect } from 'common/soql_builder/select';
import { type Expr } from 'common/types/soql';
import { type ViewColumn } from 'common/types/viewColumn';

import { type ColumnAggregation } from '../dataProviders/types';
import { type CustomSortConfig, type HierarchyColumnConfig, type OrderConfig } from '../vif';
import { GROUP_COLUMN_PREFIX, NULL_GROUPING } from '../views/agGridReact/Constants';

import { isPivotInAgRequest } from './PivotHelpers';
import { selectAlias } from './SoqlAliasHelpers';
import { countAlias, measureAlias } from './SoqlHelpers';
import { findCorrespondingColumn } from './TableDataColumnHelpers';
import { buildCasesParts, removeNullGroupKeys } from './TableDataHelpers';
import { isCustomSortEnabled, isTableAdvancedTotalingEnabled } from './VifSelectors';
import { generateCascadingPipedQuery, getAdvancedTotalingAggregations } from './TableDataFormulaHelpers';

export function generateWhereQuery(
  columns: ViewColumn[],
  groupKeys: (string | null)[],
  rowGroupCols: ColumnVO[],
): Expr | undefined {
  const whereParts: Expr[] = [];

  if (groupKeys) {
    groupKeys.forEach(function (key, i) {
      const whereColumn = findCorrespondingColumn(columns, rowGroupCols[i]);
      if (typeof whereColumn !== 'undefined') {
        if (key === null) {
          whereParts.push(f.isNull(whereColumn));
        } else {
          whereParts.push(f.eq(whereColumn, key));
        }
      }
    });
  }

  if (whereParts.length > 1) {
    return and(...whereParts);
  }

  return !isEmpty(whereParts) ? whereParts[0] : undefined;
}

export function isDoingGrouping(rowGroupCols: ColumnVO[], groupKeys: (string | null)[]) {
  // We do not want to group at the lowest level
  return rowGroupCols?.length > groupKeys?.length;
}

/**
 *
 * @param {ViewColumn []} columns - All of the columns being used in the table visualization.
 * @param {string} legacyWhereClause - The output of SoqlHelpers.whereClauseFilteringOwnColumn
 * @param {Object} agRequest - The data request information from Ag Grid.
 * @param {string} searchString - An optional search string used on the Primer
 * @returns - a query to be used in the Soql Query Builder, invokeSoql function.
 */
export function tableRowCountQuery(
  columns: ViewColumn[],
  legacyWhereClause: string,
  agRequest: IServerSideGetRowsRequest,
  searchString?: string,
) {
  const { groupKeys: agGroupKeys, rowGroupCols } = agRequest;
  const groupKeys = agGroupKeys.map((key) => (key === NULL_GROUPING || key == undefined ? null : key));

  const countSelectQuery = () => {
    let selectQueries: NamedSelect[] = [];
    if (isDoingGrouping(rowGroupCols, groupKeys)) {
      const selectCol = rowGroupCols[groupKeys.length];

      const colId = findCorrespondingColumn(columns, selectCol);
      if (typeof colId !== 'undefined') {
        selectQueries = [select(f.countDistinct(colId), countAlias())];
      }
    } else {
      selectQueries = [select(f.countStar(), countAlias())];
    }

    return selectQueries;
  };

  const rowCountQuery = composeQuery({
    selects: countSelectQuery(),
    where: generateWhereQuery(columns, groupKeys, rowGroupCols),
    search: searchString,
    legacyWhereClause,
  });

  return rowCountQuery;
}

/**
 * Used to determine if the grouping column contains a null value
 * @param {ViewColumn []} columns - All of the columns being used in the table visualization.
 * @param {string} legacyWhereClause - The output of SoqlHelpers.whereClauseFilteringOwnColumn
 * @param {Object} agRequest - The data request information from Ag Grid.
 * @returns - a query to be used in the Soql Query Builder, invokeSoql function.
 */
export function tableNullGroupingCountQuery(
  columns: ViewColumn[],
  legacyWhereClause: string,
  agRequest: IServerSideGetRowsRequest,
) {
  const { rowGroupCols, groupKeys: agGroupKeys } = agRequest;
  const groupKeys = agGroupKeys.map((key) => (key === NULL_GROUPING || key == undefined ? null : key));

  // if we're grouping, we need to check if the grouping column contains a null value;
  // if so, countDistinct will have ignored it and the resulting row count will be off by one
  // the below where clause will use the group keys as usual, but will also add a final
  // `<column> IS NULL`, <column> being the last opened column (or the top level unopened column)
  return composeQuery({
    selects: [select(f.countStar(), countAlias())],
    where: generateWhereQuery(columns, [...groupKeys, null], rowGroupCols),
    legacyWhereClause,
  });
}

// The purpose of this function is to create a map of column names to safe aliases for use in queries.
// We want to have a safe alias for each column, since SoqlBuilder requires an alias, and the field name
// may be a reserved Soql word (e.g. `group`).
export function createSelectAliasMap(
  agRequest: IServerSideGetRowsRequest,
  columns: ViewColumn[],
): { [key: string]: string } {
  const { rowGroupCols, groupKeys, pivotCols } = agRequest;
  const isPivoting = isPivotInAgRequest(agRequest);
  const aliasMap: { [key: string]: string } = {};
  let aliasCount = 0;

  if (isPivoting) {
    pivotCols.forEach((pivotCol) => {
      aliasMap[pivotCol.id] = selectAlias(aliasCount ? aliasCount : undefined);
      aliasCount += 1;
    });
  }

  // if we are making a grouping query, it's of the form SELECT a, AGG(b), AGG(c) ... WHERE ... GROUP BY a;
  if (isDoingGrouping(rowGroupCols, groupKeys)) {
    const selectCol = rowGroupCols[groupKeys.length];
    aliasMap[selectCol.id] = selectAlias(aliasCount);
  }

  if (!isEmpty(aliasMap)) {
    // We must have pivoted or grouped, so we early return
    return aliasMap;
  }

  // if we aren't grouping, we're at the lowest level, and all non-grouped columns will be part of the select
  // e.g. SELECT a, b, c, ... WHERE ...;
  const ungroupedColumns = differenceWith(columns, rowGroupCols, (c, r) => c.fieldName === r.id);
  return ungroupedColumns
    .map((col, idx) => [col.fieldName, selectAlias(idx)])
    .reduce((aliases, [colId, alias]) => ({ ...aliases, [colId]: alias }), {});
}

export function getSelectAggregationsQuery(
  valueCols: any[],
  columns: ViewColumn[],
  nonStandardAggregations?: ColumnAggregation[] | null,
): NamedSelect[] {
  const selectQueries: NamedSelect[] = [];
  const aggregations = valueCols.filter((col) => !!col.aggFunc);

  if (aggregations.length) {
    const aggregationSelects = aggregations
      .map(({ id, aggFunc }, idx) => {
        // @ts-expect-error implicit any index
        if (typeof aggFunc !== 'undefined' && typeof f[aggFunc] === 'function') {
          const viewCol = findCorrespondingColumn(columns, { id });

          // Postgresql does not allow for min or max of boolean columns,
          // so we need to use this work around.
          if (viewCol?.dataTypeName === 'checkbox' && (aggFunc === 'max' || aggFunc === 'min')) {
            // @ts-expect-error implicit any index
            return select(f.eq(f[aggFunc](f.iif(viewCol, 1, 0)), 1), measureAlias(idx));
          } else if (viewCol?.dataTypeName === 'url' && (aggFunc === 'max' || aggFunc === 'min')) {
            // @ts-expect-error implicit any index
            return select(f.url(f[aggFunc](f.url_url(viewCol)), null), measureAlias(idx));
          }

          // @ts-expect-error implicit any index
          return select(f[aggFunc](viewCol), measureAlias(idx));
        } else if (nonStandardAggregations) {
          // in the case of a non-standard aggregation, we need to use the nonStandardAggregations
          // list to find the correct SoQL expression for this column/aggregationType
          const aggregationDefinition = nonStandardAggregations.find(
            (agg) => agg.fieldName === id && agg.aggregationType === aggFunc,
          );
          if (aggregationDefinition) {
            return prerenderedSelect(aggregationDefinition.soqlExpression, measureAlias(idx));
          }
        }
      })
      .filter((selectConfig): selectConfig is NamedSelect => !!selectConfig);
    selectQueries.push(...aggregationSelects);
  }
  return selectQueries;
}

export function createMeasureAliasMap(agRequest: IServerSideGetRowsRequest): { [key: string]: string } {
  return agRequest.valueCols
    .filter((col) => !!col.aggFunc)
    .map((col, idx) => [col.id, measureAlias(idx)])
    .reduce((aliases, [colId, alias]) => {
      return { ...aliases, [alias]: colId };
    }, {});
}

export function unaliasRowData(data: any[], aliasMap: { [key: string]: string }): any[] {
  return Object.keys(aliasMap).length
    ? data.map((row) => {
        const unaliasedRow = {} as any;
        for (const colName in row) {
          if (aliasMap[colName]) {
            unaliasedRow[aliasMap[colName]] = row[colName];
          } else {
            unaliasedRow[colName] = row[colName];
          }
        }
        return unaliasedRow;
      })
    : data;
}

/**
 *
 * @param {ViewColumn []} columns - All of the columns being used in the table visualization.
 * @param {string} legacyWhereClause - The output of SoqlHelpers.whereClauseFilteringOwnColumn
 * @param {Object} agRequest - The data request information from Ag Grid.
 * @param {string} searchString - An optional search string used on Primer
 * @returns - a query to be used in the Soql Query Builder, invokeSoql function.
 */
export function tableDataQuery(
  columns: ViewColumn[],
  legacyWhereClause: string,
  agRequest: IServerSideGetRowsRequest,
  pageSize?: number,
  startIndex?: number,
  order?: OrderConfig[],
  nonStandardAggregations?: ColumnAggregation[] | null,
  searchString?: string,
  customSortConfig?: CustomSortConfig,
  hierarchyConfigs?: HierarchyColumnConfig[],
) {
  const { groupKeys: agGroupKeys, rowGroupCols, pivotCols, valueCols } = agRequest;
  const groupKeys = removeNullGroupKeys(agGroupKeys);
  const isPivoting = isPivotInAgRequest(agRequest);
  const whereParts = generateWhereQuery(columns, groupKeys, rowGroupCols);

  if (isTableAdvancedTotalingEnabled()) {
    const currentQueryGroupLevel = groupKeys.length;
    const pipedQuery = generateCascadingPipedQuery({
      columns,
      legacyWhereClause,
      agRequest,
      currentQueryGroupLevel,
      hierarchyConfigs,
      nonStandardAggregations,
      whereParts,
      pageSize,
      startIndex,
      order,
      searchString,
      customSortConfig,
    });

    if (pipedQuery) {
      return pipedQuery;
    }
  }

  const selectQuery = () => {
    const selectQueries: NamedSelect[] = [];
    const selectAliasMap: { [key: string]: string } = createSelectAliasMap(agRequest, columns);

    // select both the pivotColumn and groupColumn, if they exist
    if (isPivoting) {
      pivotCols.forEach((pivotCol) => {
        const pivotColId = findCorrespondingColumn(columns, pivotCol);
        if (typeof pivotColId !== 'undefined') {
          selectQueries.push(select(pivotColId, selectAliasMap[pivotCol.id]));
        }
      });
    }

    if (isDoingGrouping(rowGroupCols, groupKeys)) {
      const selectCol = rowGroupCols[groupKeys.length];
      const colId = findCorrespondingColumn(columns, selectCol);
      if (typeof colId !== 'undefined') {
        selectQueries.push(select(colId, selectAliasMap[selectCol.id]));
      }
    }

    // We must be pivoting or grouping, so there must be an aggregation.
    if (selectQueries.length > 0) {
      let aggregationSelects: NamedSelect[] = [];

      if (isTableAdvancedTotalingEnabled() && hierarchyConfigs) {
        // NOTE: We pass `groupKeys.length + 1` here.
        // Logic: If we are at level 0 (Top Rows), we want to apply the formulas from Level 1 (Children).
        // This ensures the custom formula bubbles UP to the group row, rather than filtering the group row itself.
        const startLevelIndex = groupKeys.length + 1;

        aggregationSelects = getAdvancedTotalingAggregations(
          valueCols,
          columns,
          hierarchyConfigs,
          startLevelIndex,
          nonStandardAggregations,
        );
      }

      if (aggregationSelects.length === 0) {
        aggregationSelects = getSelectAggregationsQuery(valueCols, columns, nonStandardAggregations);
      }

      selectQueries.push(...aggregationSelects);
      return selectQueries;
    }

    // We didn't do any grouping or pivoting, so we must be at the lowest level.
    // So just select all the columns we've added to the table
    const ungroupedColumns = differenceWith(columns, rowGroupCols, (c, r) => c.fieldName === r.id);
    each(ungroupedColumns, (col) => {
      selectQueries.push(select(col, selectAliasMap[col.fieldName]));
    });

    return selectQueries;
  };

  const groupQuery = () => {
    const groupQueries: ViewColumn[] = [];

    // group by both pivot and grouping columns, if they exist
    // so would look like `GROUP BY pivotColumn, groupColumn`
    if (isPivoting) {
      pivotCols.forEach((pivotCol) => {
        const pivotId = findCorrespondingColumn(columns, pivotCol);
        if (typeof pivotId !== 'undefined') {
          groupQueries.push(pivotId);
        }
      });
    }

    if (isDoingGrouping(rowGroupCols, groupKeys)) {
      const rowGroupCol = rowGroupCols[groupKeys.length];
      const groupId = findCorrespondingColumn(columns, rowGroupCol);
      if (typeof groupId !== 'undefined') {
        groupQueries.push(groupId);
      }
    }

    return groupQueries;
  };

  const orderByQuery = () => {
    if (isUndefined(order)) {
      return [];
    }
    const rowGroupCol = rowGroupCols[groupKeys.length];

    return order
      .map((o: OrderConfig) => {
        const col = find(
          columns,
          (c) => c.fieldName === replace(o.columnName, `${GROUP_COLUMN_PREFIX}-`, ''),
        );
        const ordering = o.ascending === true ? 'ascending' : 'descending';

        if (isUndefined(col) || col.fieldName === ':id') {
          return;
        }

        if (isDoingGrouping(rowGroupCols, groupKeys)) {
          // Table with groupings
          if (col.fieldName === rowGroupCol.id) {
            // OrderBy: Grouping column
            return orderBy(col, ordering);
          } else {
            const measureAliasMap = createMeasureAliasMap(agRequest);
            const measureAliasKey = findKey(measureAliasMap, (fieldName) => fieldName === col.fieldName);
            if (isUndefined(measureAliasKey)) {
              // OrderBy: Non Aggregated/Grouping Column, so ignored
              return;
            }
            // OrderBy: Aggregated column
            return orderBy(measureAliasKey, ordering);
          }
        } else {
          // Table without groupings
          return orderBy(col, ordering);
        }
      })
      .filter((orderForSoql) => !isUndefined(orderForSoql));
  };

  const groupedColumn = groupQuery();
  const selectParts = selectQuery();
  const orderParts = orderByQuery();
  const searchParts = searchString;

  const params = {
    selects: selectParts,
    where: whereParts,
    groups: groupedColumn,
    legacyWhereClause,
    orders: orderParts,
    limit: pageSize,
    offset: startIndex,
    search: searchParts,
    ...(isCustomSortEnabled() &&
      !isEmpty(customSortConfig) && {
        cases: buildCasesParts(selectParts, customSortConfig, order),
      }),
  };
  const dataQuery = composeQuery(params);
  return dataQuery;
}
